import { useState, useMemo, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faStore,
  faTag,
  faDollarSign,
  faBox,
  faEdit,
  faCalendarPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const EditStock = ({ stock, onUpdate, onClose, shops = [], products = [] }) => {
  const [form, setForm] = useState({
    shopId: "",
    productId: "",
    dozen_amount: "",
    purchase_price: "",
    selling_price: "",
    purchase_date: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);
  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products],
  );

  useEffect(() => {
    if (stock) {
      setForm({
        shopId: String(stock.shopId || ""),
        productId: String(stock.productId || stock.product?.productId || ""),
        dozen_amount: String(stock.dozen_amount || stock.dozenAmount || ""),
        purchase_price: String(
          stock.purchase_price || stock.purchasePrice || "",
        ),
        selling_price: String(stock.selling_price || stock.sellingPrice || ""),
        purchase_date: (() => {
          const date = stock.purchase_date || stock.purchaseDate || "";
          if (!date) return "";
          try {
            return new Date(date).toISOString().split("T")[0];
          } catch {
            return "";
          }
        })(),
      });
      setFormErrors({});
      setIsSubmitting(false);
    }
  }, [stock]);

  const selectedProduct = useMemo(() => {
    if (!form.productId) return null;
    return safeProducts.find((p) => p.productId === form.productId) || null;
  }, [safeProducts, form.productId]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TZS";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const getTotalItems = () => {
    if (!selectedProduct || !form.dozen_amount) return 0;
    return (
      parseInt(form.dozen_amount || 0) *
      (Number(selectedProduct.no_of_items) || 1)
    );
  };

  const getTotalSelling = () => {
    const totalItems = getTotalItems();
    const single = parseFloat(form.selling_price);
    if (!totalItems || !single || single <= 0) return 0;
    return single * totalItems;
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!form.shopId) errors.shopId = "Please select a shop";
    if (!form.productId) errors.productId = "Please select a product";
    if (!form.dozen_amount || parseInt(form.dozen_amount) < 1)
      errors.dozen_amount = "Please enter a valid amount";
    if (!form.purchase_price || parseFloat(form.purchase_price) < 0)
      errors.purchase_price = "Please enter a valid purchase price";
    if (!form.selling_price || parseFloat(form.selling_price) <= 0)
      errors.selling_price = "Please enter a valid selling price";
    if (!form.purchase_date)
      errors.purchase_date = "Please select a purchase date";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onUpdate({
        stockId: stock.stockId || stock.id,
        shopId: form.shopId,
        productId: form.productId,
        dozen_amount: parseInt(form.dozen_amount),
        purchase_price: parseFloat(form.purchase_price),
        selling_price: parseFloat(form.selling_price),
        purchase_date: form.purchase_date,
      });

      if (result && typeof result.then === "function") {
        try {
          await result;
          toastSuccess(
            `Stock for "${selectedProduct?.product_name || "product"}" updated successfully!`,
          );
          onClose();
        } catch (error) {
          toastError(
            `Failed to update stock: ${error?.message || "Please try again."}`,
          );
        }
      } else {
        toastSuccess(
          `Stock for "${selectedProduct?.product_name || "product"}" updated successfully!`,
        );
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to update stock: ${error?.message || "Please try again."}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit stock"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update stock
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">Edit stock</p>
            <p className="text-xs text-gray-600 font-medium">
              Update stock information
            </p>
          </div>
        </div>

        {/* Shop */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faStore}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop<span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.shopId}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, shopId: value }));
              if (formErrors.shopId)
                setFormErrors((prev) => ({ ...prev, shopId: "" }));
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${
                formErrors.shopId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select shop" />
            </SelectTrigger>
            <SelectContent>
              {safeShops.map((shop) => (
                <SelectItem key={shop.shopId} value={shop.shopId}>
                  {shop.shop_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.shopId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.shopId}
            </p>
          )}
        </div>

        {/* Product */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTag}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product<span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.productId}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, productId: value }));
              if (formErrors.productId)
                setFormErrors((prev) => ({ ...prev, productId: "" }));
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${
                formErrors.productId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {safeProducts.map((product) => (
                <SelectItem key={product.productId} value={product.productId}>
                  {product.product_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.productId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.productId}
            </p>
          )}
        </div>

        {/* Selected Product Info */}
        {selectedProduct && (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-gray-600">Product Type</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.product_category?.product_type?.name ||
                    "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Category</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.product_category?.category_name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Package Type</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.package_type || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Items per package</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.no_of_items || 1}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dozen Amount */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBox}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Amount <span className="text-red-700">*</span>
          </Label>
          <Input
            name="dozen_amount"
            type="number"
            min="1"
            value={form.dozen_amount}
            onChange={handleChange}
            placeholder="Enter number of packages"
            className={`h-10 text-sm ${
              formErrors.dozen_amount ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.dozen_amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.dozen_amount}
            </p>
          )}
          {selectedProduct && form.dozen_amount && (
            <p className="text-xs text-gray-600">
              Total Items: {getTotalItems()}
            </p>
          )}
        </div>

        {/* Purchase Price */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Purchase Price (Total) <span className="text-red-700">*</span>
          </Label>
          <Input
            name="purchase_price"
            type="number"
            min="0"
            step="0.01"
            value={form.purchase_price}
            onChange={handleChange}
            placeholder="Enter total purchase price"
            className={`h-10 text-sm ${
              formErrors.purchase_price ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.purchase_price && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.purchase_price}
            </p>
          )}
          {selectedProduct && form.dozen_amount && form.purchase_price && (
            <p className="text-xs text-gray-600">
              Cost Per Item:{" "}
              {formatCurrency(
                parseFloat(form.purchase_price) / (getTotalItems() || 1),
              )}
            </p>
          )}
        </div>

        {/* Selling Price */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Selling Price (per product) <span className="text-red-700">*</span>
          </Label>
          <Input
            name="selling_price"
            type="number"
            min="0"
            step="0.01"
            value={form.selling_price}
            onChange={handleChange}
            placeholder="Enter selling price per item (e.g., 85000)"
            className={`h-10 text-sm ${
              formErrors.selling_price ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.selling_price && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.selling_price}
            </p>
          )}
        </div>

        {/* Purchase Date */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Purchase Date <span className="text-red-700">*</span>
          </Label>
          <Input
            name="purchase_date"
            type="date"
            value={form.purchase_date}
            onChange={handleChange}
            className={`h-10 text-sm ${
              formErrors.purchase_date ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.purchase_date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.purchase_date}
            </p>
          )}
        </div>

        {/* Summary Preview */}
        {form.purchase_price &&
          form.selling_price &&
          form.dozen_amount &&
          selectedProduct && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-gray-600">Total Items</p>
                  <p className="font-bold text-gray-900">{getTotalItems()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Selling Price</p>
                  <p className="font-bold text-green-600">
                    {formatCurrency(getTotalSelling())}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Expected Profit</p>
                  <p
                    className={`font-bold ${
                      getTotalSelling() -
                        parseFloat(form.purchase_price || 0) >=
                      0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatCurrency(
                      getTotalSelling() - parseFloat(form.purchase_price || 0),
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Cost Per Item</p>
                  <p className="font-bold text-gray-900">
                    {formatCurrency(
                      parseFloat(form.purchase_price || 0) /
                        (getTotalItems() || 1),
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>
    </Modal>
  );
};

export default EditStock;
